import React, { useState } from 'react';
import { FaPlus, FaLaptop, FaBuilding, FaExchangeAlt } from 'react-icons/fa';
import LocationPicker from '../location/LocationPicker';

export default function Step8Places({ wizardData, onNext, onBack, onSave, translations, isArabic, language }) {
  const t = translations || {};
  const [lessonFormatPreference, setLessonFormatPreference] = useState(
    wizardData.lesson_format_preference || null
  );
  const [teacherLocations, setTeacherLocations] = useState(
    wizardData.teacher_locations || []
  );
  const [showLocationPicker, setShowLocationPicker] = useState(false);
  const [errors, setErrors] = useState({});

  const handleAddLocation = (locationData) => {
    const isFirstLocation = teacherLocations.length === 0;
    setTeacherLocations([...teacherLocations, {
      ...locationData,
      is_default: isFirstLocation,
      is_active: true
    }]);
    setShowLocationPicker(false);
  };

  const handleRemoveLocation = (index) => {
    setTeacherLocations(teacherLocations.filter((_, i) => i !== index));
    // Clear location error if any
    if (errors.locations) {
      setErrors(prev => ({ ...prev, locations: null }));
    }
  };

  const validate = () => {
    const newErrors = {};
    
    if (!lessonFormatPreference) {
      newErrors.lessonFormat = 'Lesson format preference is required';
    }

    // Locations are now optional - no validation needed

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validate()) {
      return;
    }

    const placesData = {
      lesson_format_preference: lessonFormatPreference,
      teacher_locations: teacherLocations
    };

    await onSave(placesData);
    onNext(placesData);
  };

  return (
    <div>
      <h2 className="step-title">{t.places || 'Places'}</h2>
      <p className="step-subtitle">
        {t.placesSubtitle || 'Choose how you prefer to deliver lessons and add locations for in-person teaching.'}
      </p>

      <form className="wizard-form" onSubmit={handleSubmit}>
        {/* Lesson Format Preference */}
        <div className="form-group">
          <label>{t.howDeliverLessons || 'How do you prefer to deliver lessons?'} *</label>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '12px' }}>
            {/* Online Only */}
            <label style={{
              display: 'flex',
              alignItems: 'center',
              padding: '16px',
              borderRadius: '12px',
              border: `2px solid ${lessonFormatPreference === 'online_only' ? '#ff7a1a' : '#e5e7eb'}`,
              background: lessonFormatPreference === 'online_only' ? '#fff5ed' : '#fff',
              cursor: 'pointer',
              transition: 'all 0.2s',
              gap: '12px'
            }}>
              <input
                type="radio"
                name="lessonFormat"
                value="online_only"
                checked={lessonFormatPreference === 'online_only'}
                onChange={(e) => setLessonFormatPreference(e.target.value)}
                style={{ width: '20px', height: '20px', cursor: 'pointer', flexShrink: 0 }}
              />
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <FaLaptop style={{ fontSize: '20px', color: lessonFormatPreference === 'online_only' ? '#ff7a1a' : '#6b7280' }} />
                <div>
                  <div style={{ fontWeight: '600', fontSize: '15px', color: '#1f2937' }}>
                    {t.onlineOnly || 'Online Only'}
                  </div>
                  <div style={{ fontSize: '13px', color: '#6b7280', marginTop: '4px' }}>
                    {t.onlineOnlyDesc || 'I will only teach online (Zoom, Google Meet, etc.)'}
                  </div>
                </div>
              </div>
            </label>

            {/* Face-to-Face Only */}
            <label style={{
              display: 'flex',
              alignItems: 'center',
              padding: '16px',
              borderRadius: '12px',
              border: `2px solid ${lessonFormatPreference === 'face_to_face_only' ? '#ff7a1a' : '#e5e7eb'}`,
              background: lessonFormatPreference === 'face_to_face_only' ? '#fff5ed' : '#fff',
              cursor: 'pointer',
              transition: 'all 0.2s',
              gap: '12px'
            }}>
              <input
                type="radio"
                name="lessonFormat"
                value="face_to_face_only"
                checked={lessonFormatPreference === 'face_to_face_only'}
                onChange={(e) => setLessonFormatPreference(e.target.value)}
                style={{ width: '20px', height: '20px', cursor: 'pointer', flexShrink: 0 }}
              />
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <FaBuilding style={{ fontSize: '20px', color: lessonFormatPreference === 'face_to_face_only' ? '#ff7a1a' : '#6b7280' }} />
                <div>
                  <div style={{ fontWeight: '600', fontSize: '15px', color: '#1f2937' }}>
                    {t.faceToFaceOnly || 'Face-to-Face Only'}
                  </div>
                  <div style={{ fontSize: '13px', color: '#6b7280', marginTop: '4px' }}>
                    {t.faceToFaceOnlyDesc || 'I will only teach in-person (at home, library, etc.)'}
                  </div>
                </div>
              </div>
            </label>

            {/* Both */}
            <label style={{
              display: 'flex',
              alignItems: 'center',
              padding: '16px',
              borderRadius: '12px',
              border: `2px solid ${lessonFormatPreference === 'both' ? '#ff7a1a' : '#e5e7eb'}`,
              background: lessonFormatPreference === 'both' ? '#fff5ed' : '#fff',
              cursor: 'pointer',
              transition: 'all 0.2s',
              gap: '12px'
            }}>
              <input
                type="radio"
                name="lessonFormat"
                value="both"
                checked={lessonFormatPreference === 'both'}
                onChange={(e) => setLessonFormatPreference(e.target.value)}
                style={{ width: '20px', height: '20px', cursor: 'pointer', flexShrink: 0 }}
              />
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <FaExchangeAlt style={{ fontSize: '20px', color: lessonFormatPreference === 'both' ? '#ff7a1a' : '#6b7280' }} />
                <div>
                  <div style={{ fontWeight: '600', fontSize: '15px', color: '#1f2937' }}>
                    {t.both || 'Both'}
                  </div>
                  <div style={{ fontSize: '13px', color: '#6b7280', marginTop: '4px' }}>
                    {t.bothDesc || 'I can teach both online or in-person (student chooses)'}
                  </div>
                </div>
              </div>
            </label>
          </div>
          
          {errors.lessonFormat && <span className="error-message">{errors.lessonFormat}</span>}
        </div>

        {/* Location Management - Show when Face-to-Face or Both is selected */}
        {(lessonFormatPreference === 'face_to_face_only' || lessonFormatPreference === 'both') && (
          <div className="form-group" style={{ marginTop: '24px', paddingTop: '24px', borderTop: '1px solid #e5e7eb' }}>
            <label>Preferred Teaching Locations</label>
            <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '12px' }}>
              Add one or more preferred locations for in-person teaching using Google Maps
            </p>

            {/* Location List */}
            {teacherLocations.length > 0 && (
              <div style={{ marginBottom: '16px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {teacherLocations.map((loc, index) => (
                  <div key={index} style={{
                    padding: '12px',
                    background: '#f9fafb',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <div>
                      <div style={{ fontWeight: '600', fontSize: '14px', color: '#1f2937' }}>
                        {loc.place_name || loc.formatted_address || `Location ${index + 1}`}
                      </div>
                      <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                        {loc.formatted_address}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemoveLocation(index)}
                      style={{
                        padding: '6px 10px',
                        border: '1px solid #dc2626',
                        borderRadius: '6px',
                        background: 'white',
                        color: '#dc2626',
                        cursor: 'pointer',
                        fontSize: '12px'
                      }}
                    >
                      {isArabic ? 'إزالة' : 'Remove'}
                    </button>
                  </div>
                ))}
              </div>
            )}

            {!showLocationPicker && (
              <button
                type="button"
                onClick={() => setShowLocationPicker(true)}
                style={{
                  padding: '10px 16px',
                  border: '1px solid #ff7a1a',
                  borderRadius: '8px',
                  background: 'white',
                  color: '#ff7a1a',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontSize: '14px',
                  fontWeight: '600'
                }}
              >
                      <FaPlus /> {t.addLocation || 'Add Location'}
              </button>
            )}

            {showLocationPicker && (
              <div style={{
                marginTop: '16px',
                padding: '16px',
                background: '#f9fafb',
                borderRadius: '12px',
                border: '1px solid #e5e7eb'
              }}>
                <div style={{ fontWeight: '600', fontSize: '14px', color: '#1f2937', marginBottom: '12px' }}>
                  {isArabic ? 'حدد موقعاً جديداً' : 'Select a New Location'}
                </div>
                <LocationPicker
                  mode="single"
                  onLocationSelect={handleAddLocation}
                  showAutocomplete={true}
                  showClickToSelect={true}
                  height="350px"
                />
                <button
                  type="button"
                  onClick={() => setShowLocationPicker(false)}
                  style={{
                    marginTop: '12px',
                    padding: '8px 16px',
                    background: 'transparent',
                    color: '#6b7280',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '13px'
                  }}
                >
                  Cancel
                </button>
              </div>
            )}

            {errors.locations && <span className="error-message">{errors.locations}</span>}
          </div>
        )}

        {/* Actions */}
        <div className="wizard-actions">
          <button type="button" className="btn btn-secondary" onClick={() => onBack({
            lesson_format_preference: lessonFormatPreference,
            teacher_locations: teacherLocations
          })}>
            {t.back || 'Back'}
          </button>
          <button type="submit" className="btn btn-primary">
            {t.saveAndContinue || 'Save and continue'}
          </button>
        </div>
      </form>
    </div>
  );
}